import "./textCounter.scss";
import { ComponentRegistry } from '@ts/utils/ComponentRegistry';

const TEXT_COUNTER_SELECTOR = '[data-text-counter]';

type TextCounterOptions = {
  warningThreshold?: number;
};

export class TextCounter {
  private static warningClass = 'text-counter-warning';
  private readonly id: string;
  private static registry = new ComponentRegistry<TextCounter>('text-counter');
  private readonly formField: HTMLInputElement | HTMLTextAreaElement;
  private readonly counterElement: HTMLSpanElement;
  private readonly warningThreshold: number;
  private readonly handler: (e: Event) => void;

  private constructor(
    formField: HTMLInputElement | HTMLTextAreaElement,
    counterElement: HTMLSpanElement,
    warningThreshold: number
  ) {
    this.id = TextCounter.registry.register(formField, this);
    this.formField = formField;
    this.counterElement = counterElement;
    this.warningThreshold = warningThreshold;
    this.handler = this.createUpdateHandler();
    this.init();
  }

  private init(): void {
    this.formField.addEventListener('input', this.handler);
    this.formField.addEventListener('focus', this.handler);
    this.updateCounterDisplay();
  }

  private createUpdateHandler = () => (e: Event): void => {
    this.updateCounterDisplay();
  };

  private updateCounterDisplay(): void {
    const maxLength = this.formField.maxLength;
    if (maxLength === -1) return;

    const currentLength = this.formField.value.length;
    const remaining = maxLength - currentLength;
    this.counterElement.textContent = remaining.toString();
    
    const remainingCharacterContainer = this.counterElement.closest('.remaining-characters');
    if (remaining <= this.warningThreshold && remainingCharacterContainer) {
      remainingCharacterContainer.classList.add(TextCounter.warningClass);
    } else {
      remainingCharacterContainer?.classList.remove(TextCounter.warningClass);
    }
  }

  public static create(
    target: string | HTMLInputElement | HTMLTextAreaElement | NodeListOf<HTMLElement> | HTMLElement[] = TEXT_COUNTER_SELECTOR,
    options: TextCounterOptions = {}
  ): TextCounter[] | null {
    const formFields = ComponentRegistry.processTargets<TextCounter, HTMLElement>(
        TextCounter.registry,
        target,
        [HTMLInputElement, HTMLTextAreaElement]
    ) as (HTMLInputElement | HTMLTextAreaElement)[];
    if (!formFields.length) return null;
    const warningThreshold = options.warningThreshold ?? 10;
    formFields.map(field => {
        const counterSelector = field.dataset.textCounter;
        if (!counterSelector) {
          console.warn('TextCounter: Form field missing data-text-counter attribute', field);
          return null;
        }

        const counterElement = document.querySelector<HTMLSpanElement>(counterSelector);
        if (!counterElement) {
          console.warn(`TextCounter: Counter element "${counterSelector}" not found for field`, field);
          return null;
        }
        return new TextCounter(field, counterElement, warningThreshold);
      }).filter((instance): instance is TextCounter => instance !== null);
  }

  public static getTextCounter(id: string): TextCounter | null {
    return TextCounter.registry.get(id) ?? null;
  }

  public destroy(): void {
    this.formField.removeEventListener('input', this.handler);
    this.formField.removeEventListener('focus', this.handler);
    TextCounter.registry.unregister(this.id);
  }
}